import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { List } from '../db/types';
import { parseQuickAdd, type ParsedQuickAdd } from '../lib/quickadd';
import './QuickAdd.css';

export interface Draft {
  raw: string;
  parsed: ParsedQuickAdd;
}

interface Props {
  lists: List[];
  onSubmit: (parsed: ParsedQuickAdd, raw: string) => void | Promise<void>;
  /**
   * Reports the live draft, or null when the field is empty. Lets a surrounding
   * screen own a Save button without duplicating the input's state.
   */
  onDraft?: (draft: Draft | null) => void;
  /** Fired when the field is entered — the shell opens its composer alongside. */
  onEnter?: () => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

/**
 * One input. Typing `buy milk tomorrow 5pm p1 #home` shows the parsed date,
 * priority and list as chips *as you type* — the raw text stays exactly as
 * written, and stays editable. Nothing is lifted out that is not shown.
 */
export function QuickAdd({
  lists,
  onSubmit,
  onDraft,
  onEnter,
  placeholder = 'Add a task or note…',
  disabled,
  autoFocus,
}: Props) {
  const [raw, setRaw] = useState('');
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  // Re-parsed on every keystroke. chrono is fast enough at one line of text
  // that debouncing would only make the chips lag behind the cursor.
  const parsed = useMemo(() => parseQuickAdd(raw, lists), [raw, lists]);

  const report = useRef(onDraft);
  report.current = onDraft;
  useEffect(() => {
    report.current?.(raw.trim() ? { raw: raw.trim(), parsed } : null);
  }, [raw, parsed]);

  async function commit() {
    const text = raw.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      await onSubmit(parsed, text);
      setRaw('');
    } finally {
      setSaving(false);
      input.current?.focus();
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commit();
    } else if (e.key === 'Escape' && raw) {
      e.preventDefault();
      e.stopPropagation();
      setRaw('');
    }
  }

  return (
    <div className={`qa${disabled ? ' is-disabled' : ''}`}>
      <div className={`qa__field${saving ? ' is-saving' : ''}`}>
        {saving ? (
          <span className="spinner qa__spinner" />
        ) : (
          <span className="qa__sigil" aria-hidden="true">
            +
          </span>
        )}
        <input
          ref={input}
          className="qa__input"
          value={raw}
          placeholder={saving ? 'Saving…' : placeholder}
          disabled={disabled || saving}
          autoFocus={autoFocus}
          spellCheck={false}
          autoComplete="off"
          aria-label="Quick add"
          onChange={(e) => setRaw(e.target.value)}
          onFocus={onEnter}
          onKeyDown={onKeyDown}
        />
      </div>

      {parsed.chips.length > 0 && (
        <div className="qa__chips">
          {parsed.chips.map((chip, i) => (
            <span className="chip" key={`${chip.kind}-${i}`}>
              {chip.kind === 'pri' ? (
                <span className={`pri-dot pri-dot--${chip.value.slice(1)}`} />
              ) : (
                <span className="chip__kind">{chip.kind}</span>
              )}
              <span className="chip__value">{chip.value}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
